import React, { useState, useRef, useEffect } from 'react';

// ═══════════════ FARE ESTIMATE CARD ═══════════════
export function FareEstimate({ fare, selected = false, onSelect }) {
  const tierLabels = { go: 'Reservo Go', comfy: 'Reservo Comfy', plus: 'Reservo Plus', vip: 'Reservo VIP' };
  const tierDescriptions = { go: 'Sedan', comfy: 'Premium', plus: 'SUV', vip: 'Luxury' };

  return (
    <div onClick={onSelect} className="p-5 rounded-2xl cursor-pointer transition-all" style={{
      border: selected ? '2px solid #2dd4a8' : '2px solid #e2e8f0',
      background: selected ? 'rgba(45,212,168,0.04)' : '#fff',
    }}>
      <div className="flex justify-between items-center mb-2">
        <div>
          <div className="font-bold text-[1.05rem] text-brand-dark">{tierLabels[fare.tier]}</div>
          <div className="text-[0.85rem] text-muted">{tierDescriptions[fare.tier]} · Up to {fare.tier === 'plus' ? 6 : 4} pax</div>
        </div>
        <div className="font-extrabold text-[1.3rem] text-brand-dark">${fare.totalFare.toFixed(2)}</div>
      </div>
      <div className="text-[0.8rem] text-muted">
        {fare.distanceKm}km · ~{fare.durationMin}min
        {fare.peakPercent > 0 && <span style={{ color: '#ff6b6b' }}> · {fare.peakPercent}% peak</span>}
      </div>
    </div>
  );
}

// ═══════════════ TRIP PIN DISPLAY (Rider only) ═══════════════
export function TripPinDisplay({ pin }) {
  if (!pin) return null;
  return (
    <div className="text-center p-6 rounded-2xl" style={{ background: 'rgba(45,212,168,0.06)', border: '1px solid rgba(45,212,168,0.2)' }}>
      <div className="text-[0.85rem] text-body mb-2">Your Trip PIN</div>
      <div className="flex justify-center gap-3">
        {String(pin).split('').map((digit, i) => (
          <div key={i} className="flex items-center justify-center font-extrabold text-brand-dark bg-white" style={{
            width: 56, height: 64, fontSize: '1.8rem', borderRadius: 12, border: '2px solid #2dd4a8',
          }}>{digit}</div>
        ))}
      </div>
      <div className="text-[0.8rem] text-muted mt-3">
        Give this PIN to your driver to start the trip
      </div>
    </div>
  );
}

// ═══════════════ TRIP PIN ENTRY (Driver only) ═══════════════
export function TripPinEntry({ onSubmit, disabled = false, attemptsRemaining = 3, locked = false, lockoutMinutes = 0 }) {
  const [digits, setDigits] = useState(['', '', '', '']);
  const [error, setError] = useState('');
  const inputsRef = useRef([]);

  const handleChange = (index, value) => {
    if (disabled || locked) return;
    const v = value.replace(/\D/g, '').slice(-1);
    const newDigits = [...digits];
    newDigits[index] = v;
    setDigits(newDigits);
    setError('');
    if (v && index < 3) inputsRef.current[index + 1]?.focus();
    if (newDigits.every((d) => d !== '')) {
      const pin = newDigits.join('');
      onSubmit?.(pin);
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 4);
    if (pasted.length === 4) {
      setDigits(pasted.split(''));
      onSubmit?.(pasted);
    }
  };

  return (
    <div className="text-center p-6">
      <div className="text-[0.85rem] text-body mb-4">
        {locked ? `Locked for ${lockoutMinutes} minutes` : disabled ? 'Drive to pickup to enter PIN' : 'Enter rider\'s 4-digit PIN'}
      </div>
      <div className="flex justify-center gap-3">
        {digits.map((digit, i) => (
          <input key={i} ref={(el) => (inputsRef.current[i] = el)} type="tel" maxLength={1} value={digit}
            onChange={(e) => handleChange(i, e.target.value)} onKeyDown={(e) => handleKeyDown(i, e)} onPaste={handlePaste}
            disabled={disabled || locked}
            className="text-center font-extrabold outline-none text-brand-dark" style={{
              width: 56, height: 64, fontSize: '1.8rem',
              borderRadius: 12, border: `2px solid ${disabled || locked ? '#e2e8f0' : '#2dd4a8'}`,
              background: disabled || locked ? '#f1f5f9' : '#fff',
            }}
          />
        ))}
      </div>
      {error && <div className="text-[0.85rem] mt-2" style={{ color: '#ff6b6b' }}>{error}</div>}
      {!locked && attemptsRemaining < 3 && (
        <div className="text-[0.8rem] mt-2" style={{ color: '#ff6b6b' }}>{attemptsRemaining} attempt{attemptsRemaining !== 1 ? 's' : ''} remaining</div>
      )}
    </div>
  );
}

// ═══════════════ GEOFENCE INDICATOR ═══════════════
export function GeofenceIndicator({ isInside, distance, hasArrived }) {
  const color = isInside ? '#2dd4a8' : hasArrived ? '#fbbf24' : '#94a3b8';
  const label = isInside ? 'At pickup' : hasArrived ? 'Left pickup zone' : distance ? `${distance}m away` : 'Locating...';

  return (
    <div className="flex items-center gap-2 px-4 py-2 rounded-full" style={{ background: `${color}15`, border: `1px solid ${color}30` }}>
      <div className="rounded-full" style={{ width: 10, height: 10, background: color, boxShadow: isInside ? `0 0 8px ${color}` : 'none' }} />
      <span className="text-[0.85rem] font-semibold" style={{ color }}>{label}</span>
    </div>
  );
}

// ═══════════════ WAITING TIMER DISPLAY ═══════════════
export function WaitingTimerDisplay({ totalSeconds, isRunning, isPaused, isInFreeWindow, waitingFee, formatTime, formatFreeRemaining, freeMinutes }) {
  const mm = Math.floor(totalSeconds / 60);
  const ss = totalSeconds % 60;
  const timeStr = `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;

  return (
    <div className="text-center p-5 rounded-2xl border border-slate-200" style={{ background: isPaused ? '#fef3cd' : '#fff' }}>
      <div className="text-[0.8rem] text-muted mb-1">
        {isPaused ? 'Timer paused — driver left pickup zone' : isRunning ? 'Waiting at pickup' : 'Waiting timer'}
      </div>
      <div className="font-extrabold" style={{ fontSize: '2.5rem', color: isInFreeWindow ? '#2dd4a8' : '#ff6b6b', fontFamily: 'monospace' }}>
        {timeStr}
      </div>
      {isInFreeWindow ? (
        <div className="text-[0.85rem]" style={{ color: '#2dd4a8' }}>Free waiting ({freeMinutes}min included)</div>
      ) : (
        <div className="text-[0.85rem]" style={{ color: '#ff6b6b' }}>Waiting fee: ${waitingFee.toFixed(2)}</div>
      )}
    </div>
  );
}

// ═══════════════ ROUTE MAP ═══════════════
// Renders a Google Maps view with route polyline, pickup/destination markers.
// routePolyline format: "lat,lng|lat,lng|..." (as stored in Firestore)
export function RouteMap({ pickup, destination, routePolyline, height = 240 }) {
  const mapRef = useRef(null);

  useEffect(() => {
    if (!mapRef.current || !window.google?.maps) return;
    if (!pickup?.lat || !destination?.lat) return;

    const map = new window.google.maps.Map(mapRef.current, {
      zoom: 12,
      center: { lat: pickup.lat, lng: pickup.lng },
      disableDefaultUI: true,
      zoomControl: true,
      gestureHandling: 'cooperative',
      styles: [
        { featureType: 'poi', stylers: [{ visibility: 'off' }] },
        { featureType: 'transit', stylers: [{ visibility: 'off' }] },
      ],
    });

    // Pickup marker (green)
    new window.google.maps.Marker({
      position: { lat: pickup.lat, lng: pickup.lng },
      map,
      icon: {
        path: window.google.maps.SymbolPath.CIRCLE,
        fillColor: '#2dd4a8',
        fillOpacity: 1,
        strokeColor: '#fff',
        strokeWeight: 2,
        scale: 8,
      },
      title: 'Pickup',
    });

    // Destination marker (red)
    new window.google.maps.Marker({
      position: { lat: destination.lat, lng: destination.lng },
      map,
      icon: {
        path: window.google.maps.SymbolPath.CIRCLE,
        fillColor: '#ff6b6b',
        fillOpacity: 1,
        strokeColor: '#fff',
        strokeWeight: 2,
        scale: 8,
      },
      title: 'Destination',
    });

    // Route polyline
    if (routePolyline) {
      const path = routePolyline.split('|').map((pair) => {
        const [lat, lng] = pair.split(',').map(Number);
        return { lat, lng };
      }).filter((p) => !isNaN(p.lat) && !isNaN(p.lng));

      if (path.length > 0) {
        new window.google.maps.Polyline({
          path,
          map,
          strokeColor: '#2dd4a8',
          strokeOpacity: 0.8,
          strokeWeight: 4,
        });

        // Fit bounds to show the whole route
        const bounds = new window.google.maps.LatLngBounds();
        path.forEach((p) => bounds.extend(p));
        bounds.extend({ lat: pickup.lat, lng: pickup.lng });
        bounds.extend({ lat: destination.lat, lng: destination.lng });
        map.fitBounds(bounds, { top: 40, right: 40, bottom: 40, left: 40 });
      }
    }
  }, [pickup, destination, routePolyline]);

  return (
    <div
      ref={mapRef}
      className="w-full rounded-xl overflow-hidden border border-slate-200"
      style={{
        height,
        background: '#f8fafc',
      }}
    />
  );
}

// ═══════════════ NAVIGATE BUTTON ═══════════════
// Deep-link to driver's preferred nav app (Google Maps, Waze, Apple Maps).
export function NavigateButton({ destination, label = 'Navigate' }) {
  const [showOptions, setShowOptions] = useState(false);

  if (!destination?.lat || !destination?.lng) return null;

  const { lat, lng } = destination;
  const links = {
    google: `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&travelmode=driving`,
    waze: `https://waze.com/ul?ll=${lat},${lng}&navigate=yes`,
    apple: `http://maps.apple.com/?daddr=${lat},${lng}&dirflg=d`,
  };

  return (
    <div className="relative">
      <button
        onClick={() => setShowOptions(!showOptions)}
        className="w-full border-0 text-white cursor-pointer font-bold"
        style={{
          padding: 14,
          borderRadius: 100,
          background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
          fontSize: '0.95rem',
        }}
      >
        🧭 {label}
      </button>
      {showOptions && (
        <div
          className="absolute left-0 right-0 bg-white border border-slate-200 overflow-hidden"
          style={{
            top: '100%',
            marginTop: 8,
            borderRadius: 12,
            boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
            zIndex: 10,
          }}
        >
          <a href={links.google} target="_blank" rel="noopener noreferrer" onClick={() => setShowOptions(false)} className="block no-underline" style={{ padding: 14, color: '#1e293b', borderBottom: '1px solid #f1f5f9' }}>📍 Google Maps</a>
          <a href={links.waze} target="_blank" rel="noopener noreferrer" onClick={() => setShowOptions(false)} className="block no-underline" style={{ padding: 14, color: '#1e293b', borderBottom: '1px solid #f1f5f9' }}>🚗 Waze</a>
          <a href={links.apple} target="_blank" rel="noopener noreferrer" onClick={() => setShowOptions(false)} className="block no-underline" style={{ padding: 14, color: '#1e293b' }}>🗺 Apple Maps</a>
        </div>
      )}
    </div>
  );
}
